import { matchesSearchTerm } from '~components/HighlightedText/matchesSearchTerm';
import { Job } from '~hooks/useJob/models';
import { Order } from '~hooks/useOrders/models';
import {
  extractWaypointData,
  formatContactText,
  makeOrderSenderDisplay,
  makeOrderTitle,
} from '~utils/orders';

// Coarse fields the dispatch omni-search "why matched" badge can attribute a
// match to, in the order the chips should render. These are derived entirely on
// the client from the data already loaded for each order (TRE-15532) — there is
// no server-provided `search_matches`. `ticket` is intentionally absent: ticket
// numbers are not modeled on the client (only counts are), so a client-side
// derivation can never light it up.
const ORDER_SEARCH_MATCH_FIELDS = [
  'order',
  'customer',
  'project',
  'site',
  'contact',
  'dispatch_number',
  'driver',
  'equipment',
  'vendor',
] as const;

type OrderSearchMatchField = (typeof ORDER_SEARCH_MATCH_FIELDS)[number];

// Collects the searchable string values behind each coarse field for a single
// order. Every value mirrors exactly the string the card renders and wraps in
// <HighlightedText /> (TRE-15530), so a badge chip and an on-card highlight can
// never disagree — matches run against the SAME display strings, not the
// underlying pieces.
//
// Field → exact card string it mirrors (KEEP IN SYNC when either side changes;
// three separate drift bugs — order/project external ids, contact, customer —
// were closed by aligning these one-to-one):
//
//   order           → makeOrderTitle(order) as rendered in the order card's
//                      title (dispatch default prependWithOrderId=false);
//                      order.orderId and order.externalId as rendered in the
//                      order card's subtitle.
//   customer        → order.account.name and the joined sender string
//                      makeOrderSenderDisplay(order), both as rendered in the
//                      order card's subtitle sender fragment. Reuses the SAME
//                      helper the card highlights, so the comma-join and the
//                      "exclude the project account" filter cannot drift.
//   project         → order.projectName and order.project.externalId, both as
//                      rendered in the order card's subtitle.
//   site            → waypoint.name + waypoint.fullAddress of the first pickup
//                      and first drop-off, as rendered by the dispatch card's
//                      waypoint component. Short thoroughfare address is never
//                      rendered, so it is never matched.
//   contact         → formatContactText(name, phone) per contact, as rendered
//                      by the dispatch card's Contact fragment.
//   dispatch_number → order.dispatchNumber.name, which makeOrderTitle folds
//                      into the highlighted order title — so this chip always
//                      corresponds to a real title highlight and lights up
//                      alongside the "order" chip by construction.
//   driver /        → NOT highlighted on the collapsed card at all: these live
//   equipment /        on the order's jobs (loaded separately), not on the Order
//   vendor             model, and no card fragment wraps them in <HighlightedText>.
//                      Kept as a best-effort chip (matched against the job values
//                      when jobs are loaded, no values otherwise — graceful
//                      degradation); intentionally NOT aligned to a card string
//                      because the card shows none. See ORDER_SEARCH_MATCH_FIELDS.
//   ticket          → absent entirely (see ORDER_SEARCH_MATCH_FIELDS): ticket
//                      numbers are not modeled client-side, so nothing to mirror.
function collectSearchableValuesByField(
  order: Order,
  jobs: Job[],
): Record<OrderSearchMatchField, Array<string | null | undefined>> {
  // The card renders only the first pickup and first drop-off waypoint, and only
  // their name and full address (BaseWaypointComponent) — never the short
  // thoroughfare address. Match those exact strings so a site chip always
  // corresponds to a visible highlight.
  const siteWaypoints = [order.pickUpWayPoints?.[0], order.dropOffWayPoints?.[0]];
  const siteValues = siteWaypoints.flatMap((waypoint) => {
    const waypointData = extractWaypointData(waypoint);

    return waypointData ? [waypointData.name, waypointData.fullAddress] : [];
  });

  // Mirror the exact contact strings the Contact fragment renders: foremen and
  // collaborators use `User.fullName`, the extended contacts (sales / foreman /
  // collaborator / supervisor, exposed as `order.allContacts`) use
  // `ContactItem.name`. Reading the same getters the card reads is what keeps a
  // chip and an on-card highlight in lockstep (TRE-15532).
  const contactValues = [
    ...order.foremen.map((foreman) => formatContactText(foreman.fullName, foreman.phone)),
    ...(order.collaborators ?? []).map((collaborator) =>
      formatContactText(collaborator.fullName, collaborator.phone),
    ),
    ...order.allContacts.map((contact) => formatContactText(contact.name, contact.phone)),
  ];

  return {
    // The card renders order identity as the order-card title (name / dispatch
    // number / drop-off fallback, via makeOrderTitle with the dispatch card's
    // default prependWithOrderId=false) plus the order id and external id in the
    // subtitle. Matching the assembled title — not its pieces — lets a term that
    // spans the title's separators still produce a chip.
    order: [makeOrderTitle(order), order.orderId, order.externalId],
    // The subtitle highlights the project account name and, when there are
    // distinct senders, ONE comma-joined sender string (not each name on its
    // own). Mirror both via the same helper the card renders so a term spanning
    // the ", " join still lights the customer chip in step with the highlight.
    customer: [order.account?.name, makeOrderSenderDisplay(order)],
    // The subtitle renders BOTH the project name (`order.projectName`) and, on a
    // separate line, the project external id (`order.project.externalId`), each
    // wrapped in <HighlightedText /> (OrderCardSubtitle). Include both so a match
    // on either one lights the project chip in step with the visible highlight.
    project: [order.projectName, order.project?.externalId],
    site: siteValues,
    contact: contactValues,
    dispatch_number: [order.dispatchNumber?.name],
    driver: jobs.map((job) => job.driverFullName),
    equipment: jobs.map((job) => job.equipment?.name),
    vendor: jobs.map((job) => job.vendorJobAssignment?.vendorAccount?.name),
  };
}

// Returns the coarse field keys whose data contains the search term. An empty or
// whitespace-only term matches nothing.
export function deriveOrderSearchMatchFields(
  order: Order,
  jobs: Job[],
  searchTerm: string,
) {
  if (!searchTerm.trim()) {
    return [];
  }

  const valuesByField = collectSearchableValuesByField(order, jobs);

  return ORDER_SEARCH_MATCH_FIELDS.filter((field) =>
    valuesByField[field].some(
      (value) => typeof value === 'string' && matchesSearchTerm(value, searchTerm),
    ),
  );
}
